import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  Wallet,
  LineChart,
  Download,
  HeadphonesIcon,
  CheckCircle2,
  Building2,
  Hash,
  Link2,
  Activity,
  Globe,
  Banknote,
  Zap,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

// ---------------------------------------------------------------------------
// Partner Benefits — page is exclusively for Partner Organizations.
// Restaurant benefits live ONLY on /partners/member-benefits.
// ---------------------------------------------------------------------------

function BenefitTile({ n, icon: Icon, title, body, accent, bullets, examples, code, examplesLabel, exampleLabel }) {
  return (
    <div
      className={`relative rounded-2xl border p-7 transition-all hover:border-gold-500/40 ${
        accent
          ? 'bg-gradient-to-br from-gold-500/[0.08] to-amber-500/[0.04] border-gold-500/30'
          : 'bg-white/[0.025] border-white/[0.06]'
      }`}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gold-500/[0.04] blur-3xl rounded-full pointer-events-none" />
      <div className="flex items-start justify-between mb-5">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center ${
            accent
              ? 'bg-gold-500 text-black'
              : 'bg-gold-500/10 border border-gold-500/20 text-gold-300'
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/35 font-bold">
          {String(n).padStart(2, '0')}
        </span>
      </div>
      <h3 className="text-lg font-bold text-white mb-2 leading-tight">{title}</h3>
      <p className="text-sm text-white/65 leading-relaxed">{body}</p>

      {bullets && (
        <ul className="mt-4 pt-4 border-t border-white/[0.06] space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-2 text-xs text-white/70">
              <CheckCircle2 className="w-3.5 h-3.5 text-gold-300 flex-shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      )}

      {examples && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-2">{examplesLabel}</p>
          <div className="flex flex-wrap gap-1.5">
            {examples.map((ex) => (
              <span key={ex} className="font-mono text-[11.5px] px-2 py-1 rounded-md bg-black/40 border border-gold-500/15 text-gold-200">
                {ex}
              </span>
            ))}
          </div>
        </div>
      )}

      {code && (
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-2">{exampleLabel}</p>
          <div className="font-mono text-[12px] px-3 py-2 rounded-lg bg-black/50 border border-white/[0.06] text-gold-200 break-all">
            {code}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnersPartnerBenefits() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}

function Inner() {
  const { t } = useTranslation();

  const BENEFITS = [
    { n: 1, icon: TrendingUp, title: t('partners.pbenefits.b1_title'), body: t('partners.pbenefits.b1_body'), accent: true },
    {
      n: 2, icon: Wallet, title: t('partners.pbenefits.b2_title'), body: t('partners.pbenefits.b2_body'),
      bullets: [t('partners.pbenefits.b2_li_1'), t('partners.pbenefits.b2_li_2'), t('partners.pbenefits.b2_li_3')],
    },
    {
      n: 3, icon: Hash, title: t('partners.pbenefits.b3_title'), body: t('partners.pbenefits.b3_body'),
      examples: ['PIZZAASSOC', 'MASSRESTAURANT', 'FOODTRUCKBOSTON'],
      examplesLabel: t('partners.pbenefits.b3_examples_label'),
    },
    {
      n: 4, icon: Link2, title: t('partners.pbenefits.b4_title'), body: t('partners.pbenefits.b4_body'),
      code: 'nofee.restaurant/signup?partner=MASSRESTAURANT',
      exampleLabel: t('partners.pbenefits.b4_example_label'),
    },
    {
      n: 5, icon: LineChart, title: t('partners.pbenefits.b5_title'), body: t('partners.pbenefits.b5_body'),
      bullets: [
        t('partners.pbenefits.b5_li_1'),
        t('partners.pbenefits.b5_li_2'),
        t('partners.pbenefits.b5_li_3'),
        t('partners.pbenefits.b5_li_4'),
        t('partners.pbenefits.b5_li_5'),
        t('partners.pbenefits.b5_li_6'),
      ],
    },
    {
      n: 6, icon: Download, title: t('partners.pbenefits.b6_title'), body: t('partners.pbenefits.b6_body'),
      bullets: [
        t('partners.pbenefits.b6_li_1'),
        t('partners.pbenefits.b6_li_2'),
        t('partners.pbenefits.b6_li_3'),
        t('partners.pbenefits.b6_li_4'),
        t('partners.pbenefits.b6_li_5'),
        t('partners.pbenefits.b6_li_6'),
      ],
    },
    { n: 7, icon: HeadphonesIcon, title: t('partners.pbenefits.b7_title'), body: t('partners.pbenefits.b7_body') },
    {
      n: 8, icon: Activity, title: t('partners.pbenefits.b8_title'), body: t('partners.pbenefits.b8_body'),
      bullets: [
        t('partners.pbenefits.b8_li_1'),
        t('partners.pbenefits.b8_li_2'),
        t('partners.pbenefits.b8_li_3'),
        t('partners.pbenefits.b8_li_4'),
      ],
    },
  ];

  const PAYOUT_HIGHLIGHTS = [
    t('partners.pbenefits.pay_li_1'),
    t('partners.pbenefits.pay_li_2'),
    t('partners.pbenefits.pay_li_3'),
    t('partners.pbenefits.pay_li_4'),
    t('partners.pbenefits.pay_li_5'),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'NoFee Partners — Benefits For Partner Organizations',
    provider: { '@type': 'Organization', name: 'NoFee', url: 'https://nofee.restaurant' },
    description:
      '10% recurring revenue, automatic Stripe payouts, partner dashboard, referral tracking and marketing materials for organizations referring restaurants to NoFee.',
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO
        title="Partner Benefits — NoFee Partners"
        description="Generate recurring revenue while helping your members reduce costs. 10% recurring revenue, automatic Stripe payouts, partner dashboard and more."
        path="/partners/partner-benefits"
        jsonLd={jsonLd}
      />
      <Navbar forceSolid />

      <main className="pt-32 lg:pt-40 pb-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <Link
            to="/partners"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6 transition-colors"
            data-testid="partner-benefits-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t('partners.pbenefits.back')}
          </Link>

          {/* Page header */}
          <div className="max-w-[820px] mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/[0.08] border border-gold-500/25 text-[11.5px] uppercase tracking-[0.22em] text-gold-300 font-bold mb-5">
              <Building2 className="w-3.5 h-3.5" /> {t('partners.pbenefits.kicker')}
            </div>
            <h1
              className="text-4xl lg:text-6xl font-black tracking-[-0.02em] text-white leading-[1.04]"
              data-testid="partner-benefits-title"
            >
              {t('partners.pbenefits.title_a')} <span className="bg-gradient-to-b from-amber-200 to-gold-400 bg-clip-text text-transparent">{t('partners.pbenefits.title_b')}</span>.
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-[680px] leading-relaxed">
              {t('partners.pbenefits.subtitle')}
            </p>
          </div>

          {/* 8 numbered benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-benefits-grid">
            {BENEFITS.map((b) => (
              <BenefitTile key={b.n} {...b} />
            ))}
          </div>

          {/* HOW YOU GET PAID */}
          <section className="mt-20 lg:mt-28" data-testid="partner-benefits-payments">
            <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
                  {t('partners.pbenefits.pay_label')}
                </p>
                <h2 className="text-3xl lg:text-5xl font-black tracking-[-0.015em] text-white leading-[1.05]">
                  {t('partners.pbenefits.pay_title_a')} <span className="text-gold-300">{t('partners.pbenefits.pay_title_b')}</span>.
                </h2>
                <p className="mt-5 text-white/65 leading-relaxed max-w-[460px]">
                  {t('partners.pbenefits.pay_subtitle')}
                </p>
              </div>
              <div className="rounded-3xl border border-gold-500/25 bg-gradient-to-br from-gold-500/[0.05] to-black/30 p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gold-500 text-black flex items-center justify-center">
                    <Banknote className="w-5 h-5" />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-gold-300 font-bold">
                    {t('partners.pbenefits.pay_powered')}
                  </p>
                </div>
                <ul className="space-y-3.5">
                  {PAYOUT_HIGHLIGHTS.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-[15px] text-white/85">
                      <CheckCircle2 className="w-5 h-5 text-gold-300 flex-shrink-0 mt-0.5" />
                      <span className="font-semibold">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* WHY CONNECT STRIPE */}
          <section className="mt-20" data-testid="partner-benefits-why-stripe">
            <div className="rounded-3xl border border-white/[0.08] bg-gradient-to-br from-zinc-900/60 to-black/30 p-8 lg:p-12">
              <div className="grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-10 items-start">
                <div className="w-14 h-14 rounded-2xl bg-[#635bff]/15 border border-[#635bff]/40 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-7 h-7 text-[#a39cff]" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#a39cff] font-bold mb-2">
                    {t('partners.pbenefits.why_label')}
                  </p>
                  <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-white mb-3 leading-tight">
                    {t('partners.pbenefits.why_title')}
                  </h2>
                  <p className="text-white/65 leading-relaxed max-w-[680px] mb-5">
                    {t('partners.pbenefits.why_body_a')} <strong className="text-white">{t('partners.pbenefits.why_body_b')}</strong>
                  </p>
                  <div className="flex items-center gap-2 text-xs text-white/55">
                    <Globe className="w-3.5 h-3.5 text-[#a39cff]" />
                    <span>{t('partners.pbenefits.why_trusted')}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA strip */}
          <div className="mt-20 rounded-3xl border border-gold-500/25 bg-gradient-to-br from-zinc-900 to-black p-8 lg:p-12">
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
                  {t('partners.pbenefits.cta_label')}
                </p>
                <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-white leading-tight">
                  {t('partners.pbenefits.cta_title')}
                </h2>
                <p className="mt-4 text-white/60 max-w-[560px] leading-relaxed">
                  {t('partners.pbenefits.cta_subtitle')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/partners/onboarding"
                  className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] transition-all"
                  data-testid="partner-benefits-cta-apply"
                >
                  {t('partners.pbenefits.cta_apply')} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/partners/member-benefits"
                  className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-gold-500/40 text-white font-bold text-[14px] transition-all"
                  data-testid="partner-benefits-cta-member"
                >
                  {t('partners.pbenefits.cta_member')} <CheckCircle2 className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
